import { InSessionFactory } from '@/bridge/factory/InSessionFactory';
import {
  OnErrorFn,
  SessionState,
  SoloNativeRTCChannel,
} from '@amzn/work-spaces-web-client-core-lib';
import { useSessionStore } from '@/stores/session';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const reportHeartbeatTask = InSessionFactory.getReportHeartbeatTask();

export interface HeartbeatErrorControls {
  retryAfter: (delayMs: number) => void;
}

interface UseReportHeartbeatTaskOptions {
  onError?: (error: unknown, controls: HeartbeatErrorControls) => void;
}

export const useReportHeartbeatTask = ({
  onError: onErrorOverride,
}: UseReportHeartbeatTaskOptions = {}) => {
  const navigate = useNavigate();
  const heartBeatEndpoint = useSessionStore((state) => state.heartBeatEndpoint);
  const sessionState = useSessionStore((state) => state.sessionState);
  const [runTask, setRunTask] = useState(false);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const startReportingHeartbeat = () => {
    setRunTask(true);
  };

  const clearRetryTimer = () => {
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
  };

  const onError: OnErrorFn = onErrorOverride
    ? (error: unknown) =>
        onErrorOverride(error, {
          retryAfter: (delayMs: number) => {
            clearRetryTimer();
            retryTimerRef.current = setTimeout(() => {
              retryTimerRef.current = null;
              reportHeartbeatTask.start({ onError });
            }, delayMs);
          },
        })
    : (_error: unknown) => {
        navigate('/disconnect'); // TODO: disconnect should be able to show error
      };

  useEffect(() => {
    if (SoloNativeRTCChannel.isChannelAvailable()) {
      if (sessionState === SessionState.STREAMING) {
        setRunTask(true);
      } else if (sessionState === SessionState.DISCONNECT) {
        setRunTask(false);
      }
    }
  }, [sessionState]);

  useEffect(() => {
    if (runTask && heartBeatEndpoint) {
      reportHeartbeatTask.start({ onError });
    } else {
      reportHeartbeatTask.stop();
    }

    return () => {
      clearRetryTimer();
      reportHeartbeatTask.stop();
    };
  }, [runTask, heartBeatEndpoint]);

  return {
    startReportingHeartbeat,
  };
};
